import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Users, UserCheck, Eye, Sword, TrendingUp, Calendar, ChevronDown, ChevronUp, BarChart3 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Store guest ID and alias in localStorage
const getGuestId = () => {
  let guestId = localStorage.getItem("guest_id");
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("guest_id", guestId);
  }
  return guestId;
};

// Get guest alias (set by server, stored locally)
export const getGuestAlias = () => {
  return localStorage.getItem("guest_alias") || null;
};

// Set guest alias
const setGuestAlias = (alias) => {
  if (alias) {
    localStorage.setItem("guest_alias", alias);
  }
};

const SiteStatsWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [siteStats, setSiteStats] = useState(null);
  const [killStats, setKillStats] = useState(null);
  const [showOnlineList, setShowOnlineList] = useState(false);
  const [showKillDetails, setShowKillDetails] = useState(false);
  const [killPeriod, setKillPeriod] = useState("today"); // "today", "week", "month"

  // Fetch site stats and send heartbeat
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [siteRes, killsRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats/site`),
          axios.get(`${API_URL}/api/stats/kills-widget`)
        ]);
        setSiteStats(siteRes.data);
        setKillStats(killsRes.data);
      } catch (error) {
        console.error("Failed to fetch site stats:", error);
      }
    };

    // Send guest heartbeat if not logged in
    const sendHeartbeat = async () => {
      if (!user) {
        try {
          const response = await axios.post(`${API_URL}/api/stats/guest-heartbeat`, {
            guest_id: getGuestId()
          });
          // Store the guest alias returned by the server
          if (response.data.guest_alias) {
            setGuestAlias(response.data.guest_alias);
          }
        } catch (error) {
          console.error("Failed to send heartbeat:", error);
        }
      }
    };

    fetchStats();
    sendHeartbeat();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStats();
      sendHeartbeat();
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const getKillCount = () => {
    if (!killStats) return 0;
    switch (killPeriod) {
      case "today": return killStats.today_total || 0;
      case "week": return killStats.week_total || 0;
      case "month": return killStats.month_total || 0;
      default: return killStats.today_total || 0;
    }
  };

  const getKillsByBoss = () => {
    if (!killStats) return {};
    switch (killPeriod) {
      case "today": return killStats.today_by_boss || {};
      case "week": return killStats.week_by_boss || {};
      case "month": return killStats.month_by_boss || {};
      default: return killStats.today_by_boss || {};
    }
  };

  const getPeriodLabel = () => {
    switch (killPeriod) {
      case "today": return "Today";
      case "week": return "This Week";
      case "month": return "This Month";
      default: return "Today";
    }
  };

  const getPeriodIcon = () => {
    switch (killPeriod) {
      case "today": return <Sword className="w-4 h-4 text-red-400" />;
      case "week": return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case "month": return <Calendar className="w-4 h-4 text-amber-400" />;
      default: return <Sword className="w-4 h-4 text-red-400" />;
    }
  };

  const getPeriodColor = () => {
    switch (killPeriod) {
      case "today": return "text-red-400";
      case "week": return "text-emerald-400";
      case "month": return "text-amber-400";
      default: return "text-red-400";
    }
  };

  return (
    <div className="space-y-3" data-testid="site-stats-widget">
      {/* Online Hunters */}
      <div className="bg-background/50 rounded-lg p-3 border border-border/50">
        <button 
          onClick={() => setShowOnlineList(!showOnlineList)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Online Hunters</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-emerald-400">
              {siteStats?.online_count || 0}
            </span>
            {showOnlineList ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </button>
        
        {showOnlineList && siteStats?.online_hunters?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
            {siteStats.online_hunters.slice(0, 10).map((hunter, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {hunter.avatar ? (
                  <img 
                    src={hunter.avatar} 
                    alt={hunter.username}
                    className="w-4 h-4 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-4 h-4 bg-primary/20 rounded-full"></div>
                )}
                <span className="text-muted-foreground truncate">{hunter.username}</span>
              </div>
            ))}
            {siteStats.online_hunters.length > 10 && (
              <p className="text-xs text-muted-foreground">
                +{siteStats.online_hunters.length - 10} more
              </p>
            )}
          </div>
        )}
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-background/50 rounded-lg p-2 border border-border/50">
          <div className="flex items-center gap-1.5">
            <UserCheck className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs text-muted-foreground">Registered</span>
          </div>
          <p className="text-lg font-bold mt-1">{siteStats?.total_registered || 0}</p>
        </div>
        <div className="bg-background/50 rounded-lg p-2 border border-border/50">
          <div className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs text-muted-foreground">Guests</span>
          </div>
          <p className="text-lg font-bold mt-1">{siteStats?.guest_count || 0}</p>
        </div>
      </div>

      {/* Global Kills - With Period Toggle */}
      <div className="bg-background/50 rounded-lg p-3 border border-border/50">
        {/* Period Toggle Buttons */}
        <div className="flex gap-1 mb-3">
          <button
            onClick={() => setKillPeriod("today")}
            className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              killPeriod === "today" 
                ? "bg-red-500/20 text-red-400 border border-red-500/50" 
                : "bg-background/50 text-muted-foreground hover:text-foreground"
            }`}
          >
            Day
          </button>
          <button
            onClick={() => setKillPeriod("week")}
            className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              killPeriod === "week" 
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50" 
                : "bg-background/50 text-muted-foreground hover:text-foreground"
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setKillPeriod("month")}
            className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              killPeriod === "month" 
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/50" 
                : "bg-background/50 text-muted-foreground hover:text-foreground"
            }`}
          >
            Month
          </button>
        </div>

        <button 
          onClick={() => setShowKillDetails(!showKillDetails)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            {getPeriodIcon()}
            <span className="text-sm font-medium">Kills {getPeriodLabel()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${getPeriodColor()}`}>
              {getKillCount()}
            </span>
            {showKillDetails ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {showKillDetails && (
          <div className="mt-2 pt-2 border-t border-border/50 space-y-2">
            {/* Kills by boss */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">By Boss:</p>
              {Object.entries(getKillsByBoss()).slice(0, 5).map(([boss, count]) => (
                <div key={boss} className="flex justify-between text-xs">
                  <span className="truncate text-muted-foreground">{boss.split(" - ")[0]}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
              {Object.keys(getKillsByBoss()).length === 0 && (
                <p className="text-xs text-muted-foreground italic">No kills recorded</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Stats Button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full gap-2"
        onClick={() => navigate("/global-stats")}
      >
        <BarChart3 className="w-4 h-4" />
        Advanced Global Stats
      </Button>
    </div>
  );
};

export default SiteStatsWidget;
